import MaskedView from "@react-native-masked-view/masked-view";
import { LinearGradient } from "expo-linear-gradient";
import { StyleSheet, View, type StyleProp, type ViewStyle } from "react-native";

import { PageBackdrop } from "@/components/PageBackdrop";
import { palette } from "@/design/tokens";

type BackdropScrimVariant = "header" | "top-edge" | "bottom-edge";

const maskByVariant = {
  header: {
    colors: ["black", "black", "transparent"] as const,
    locations: [0, 0.05, 1] as const
  },
  "top-edge": {
    colors: ["black", "transparent"] as const
  },
  "bottom-edge": {
    colors: ["transparent", "black"] as const
  }
};

/**
 * A small hardware-only window onto the persistent page backdrop. The caller
 * may translate the surface so the copy remains pixel-aligned with the real
 * full-screen backdrop behind route content.
 */
export function BackdropScrim({
  sourceUri,
  style,
  surfaceStyle,
  variant
}: {
  sourceUri?: string | null;
  style?: StyleProp<ViewStyle>;
  surfaceStyle?: StyleProp<ViewStyle>;
  variant: BackdropScrimVariant;
}) {
  const mask = maskByVariant[variant];

  return (
    <MaskedView
      androidRenderingMode="hardware"
      pointerEvents="none"
      style={style}
      maskElement={
        <LinearGradient
          colors={mask.colors}
          locations={"locations" in mask ? mask.locations : undefined}
          style={StyleSheet.absoluteFill}
        />
      }
    >
      <View style={[styles.backdropSurface, surfaceStyle]}>
        <PageBackdrop sourceUri={sourceUri} />
      </View>
    </MaskedView>
  );
}

/**
 * Android's fixed-header copy of the one persistent page backdrop.
 *
 * The scrolling route itself is never masked. This small, hardware-only cap
 * sits above it and fades the shared backdrop toward the ordinary page, so
 * native-stack push/pop only ever animates transparent route content.
 */
export function HeaderBackdropScrim({
  sourceUri,
  style
}: {
  sourceUri?: string | null;
  style?: StyleProp<ViewStyle>;
}) {
  return (
    <BackdropScrim
      sourceUri={sourceUri}
      style={style}
      variant="header"
    />
  );
}

const styles = StyleSheet.create({
  backdropSurface: {
    backgroundColor: palette.bg,
    flex: 1
  }
});
